/**
 * Cart Upsell
 *
 * cart-upsell-item — custom element that handles the add-to-cart action
 * for individual upsell product cards (both desktop panel and mobile carousel).
 *
 * After a successful add, dispatches 'cart:update' so the cart drawer
 * and cart bubble count refresh automatically.
 *
 * Listens to the theme's 'cart:update' event (same event used by CartItemsComponent)
 * and re-fetches the cart-upsell section via the Section Rendering API to keep
 * the upsell list in sync with the current cart state at all times.
 */

if (!customElements.get('cart-upsell-item')) {
  customElements.define(
    'cart-upsell-item',
    class CartUpsellItem extends HTMLElement {
      connectedCallback() {
        this._btn = this.querySelector('[data-add-btn]');
        if (this._btn) {
          this._handleAdd = this._onAdd.bind(this);
          this._btn.addEventListener('click', this._handleAdd);
        }
      }

      disconnectedCallback() {
        this._btn?.removeEventListener('click', this._handleAdd);
      }

      async _onAdd() {
        const variantId = this.dataset.variantId;
        if (!variantId || this.hasAttribute('data-added')) return;

        this._btn.disabled = true;

        try {
          // Collect cart-items section IDs so Shopify returns fresh rendered HTML
          // in the response — cart-items-component uses it to morph the drawer instantly.
          const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
            .map((el) => el.dataset.sectionId)
            .filter(Boolean);

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1, sections: sectionIds }),
          });

          if (!addRes.ok) throw new Error(`cart/add.js ${addRes.status}`);

          const response = await addRes.json();

          this._markAdded();

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: response,
                data: { sections: response.sections ?? {} },
              },
            })
          );

          // The cart morph above runs synchronously — fade in line item images it just swapped
          this._fadeInSwappedCartImages();
        } catch (err) {
          console.error('[cart-upsell]', err);
          this._btn.disabled = false;
        }
      }

      _markAdded() {
        this.setAttribute('data-added', '');
        this._btn.disabled = true;
      }

      /**
       * Cart rows have no id, so morph reuses rows by position: the new product lands in
       * the first row and only its img src changes — the browser keeps showing the previous
       * product's photo until the new one loads. Hide still-loading images, fade in on load.
       */
      _fadeInSwappedCartImages() {
        document.querySelectorAll('cart-items-component .cart-items__media-image').forEach((img) => {
          if (img.complete) return;

          img.classList.add('cu-img-loading');
          const reveal = () => img.classList.remove('cu-img-loading');
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
          setTimeout(reveal, 3000); // safety net if load never fires
        });
      }
    },
  );
}

// ── Cart page row (blocks/cart-upsell.liquid) ─────────────────────────────────
// Owns its own Swiper instance. main-cart morph replaces this element only when
// the offered products change, so connect/disconnect handles (re)init + cleanup.

if (!customElements.get('cart-upsell-row')) {
  customElements.define(
    'cart-upsell-row',
    class CartUpsellRow extends HTMLElement {
      connectedCallback() {
        if (typeof Swiper !== 'undefined') {
          this._initSwiper();
        } else {
          this._onLoad = () => this._initSwiper();
          window.addEventListener('load', this._onLoad, { once: true });
        }
      }

      disconnectedCallback() {
        if (this._onLoad) window.removeEventListener('load', this._onLoad);
        this._swiper?.destroy(true, true);
        this._swiper = null;
      }

      _initSwiper() {
        const el = this.querySelector('.cu-row__swiper');
        if (!el || this._swiper || typeof Swiper === 'undefined') return;

        // Keep in sync with the pre-init fallback widths in cart-upsell.css (.cu-row__swiper)
        this._swiper = new Swiper(el, {
          slidesPerView: 2.2,
          spaceBetween: 10,
          grabCursor: true,
          // No touchStartPreventDefault: false here (unlike the drawer carousel) — on desktop it
          // lets the browser start a native image/link drag, Swiper never gets the mouseup and
          // the row keeps following the cursor until the next click.
          freeMode: { enabled: true, sticky: false },
          navigation: {
            prevEl: this.querySelector('[data-prev]'),
            nextEl: this.querySelector('[data-next]'),
          },
          breakpoints: {
            750: { slidesPerView: 4.3, spaceBetween: 14 },
            1100: { slidesPerView: 5.3, spaceBetween: 16 },
          },
        });
      }
    },
  );
}

// ── Dynamic re-render via Section Rendering API ───────────────────────────────
// Listens to cart:update (dispatched by CartUpsellItem above, quantity changes,
// item removals — anything that modifies the cart). Re-fetches sections/cart-upsell.liquid
// so that the panel and carousel always reflect the current cart state.

let _upsellSwiper = null;
let _upsellTimer = null;

function _scheduleUpsellRefresh() {
  // Cart page has no drawer containers — its row is re-rendered by main-cart itself
  if (!document.querySelector('[data-upsell-panel], [data-upsell-carousel]')) return;

  clearTimeout(_upsellTimer);
  _upsellTimer = setTimeout(_doUpsellRefresh, 80);
}

async function _doUpsellRefresh() {
  try {
    const res = await fetch('/?sections=cart-upsell');
    if (!res.ok) throw new Error(`sections fetch ${res.status}`);

    const data = await res.json();
    const parser = new DOMParser();
    const doc = parser.parseFromString(data['cart-upsell'], 'text/html');

    const livePanel = document.querySelector('[data-upsell-panel]');
    const liveCarousel = document.querySelector('[data-upsell-carousel]');
    const newPanel = doc.querySelector('[data-upsell-panel]');
    const newCarousel = doc.querySelector('[data-upsell-carousel]');

    if (_upsellSwiper) {
      _upsellSwiper.destroy(true, true);
      _upsellSwiper = null;
    }

    if (livePanel && newPanel) livePanel.replaceWith(newPanel);
    if (liveCarousel && newCarousel) liveCarousel.replaceWith(newCarousel);

    initUpsellSwiper();
  } catch (err) {
    console.error('[cart-upsell] refresh failed', err);
  }
}

document.addEventListener('cart:update', _scheduleUpsellRefresh);

// ── Swiper init ───────────────────────────────────────────────────────────────

function initUpsellSwiper() {
  const SLIDES_PER_VIEW = 2.2;  // how many slides are visible at once
  const SPACE_BETWEEN   = 10;   // gap between slides in px

  document.querySelectorAll('.cu-swiper:not([data-swiper-init])').forEach(function (el) {
    el.setAttribute('data-swiper-init', '');
    _upsellSwiper = new Swiper(el, {
      slidesPerView: SLIDES_PER_VIEW,
      spaceBetween: SPACE_BETWEEN,
      grabCursor: true,
      touchStartPreventDefault: false,
      freeMode: {
        enabled: true,
        sticky: false,
      },
    });
  });
}

if (typeof Swiper !== 'undefined') {
  initUpsellSwiper();
} else {
  window.addEventListener('load', initUpsellSwiper, { once: true });
}
